import React, { useRef, useState } from "react";
import { HiOutlineX, HiOutlineUpload } from "react-icons/hi";
import { toast } from "react-hot-toast";
import API from "../../api";
import { capture } from "../../instrument";

const UploadPrescriptionModal = ({ onClose, onUploaded }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [formMode, setFormMode] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const uploadFiles = async (fileList) => {
    if (!fileList || fileList.length === 0 || isUploading) return;
    const files = Array.from(fileList);
    setIsUploading(true);
    const toastId = toast.loading(`Extraction en cours... (0/${files.length})`);
    try {
      const createdIds = [];
      for (let i = 0; i < files.length; i++) {
        toast.loading(`Extraction en cours... (${i + 1}/${files.length})`, { id: toastId });

        const formData = new FormData();
        formData.append("file", files[i]);
        formData.append("formMode", formMode);

        const response = await API.postFormData("/v1/prescription", formData);
        if (!response.ok) throw new Error(response.message);
        createdIds.push(response.data._id);
      }
      toast.success(`${createdIds.length} ordonnance(s) extraite(s)`, { id: toastId });
      onUploaded(createdIds);
    } catch (error) {
      capture(error);
      toast.error(error.message || "Une erreur est survenue", { id: toastId });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[220] flex items-center justify-center bg-black/20" onMouseDown={isUploading ? undefined : onClose}>
      <div role="dialog" aria-modal="true" className="w-full max-w-lg rounded-lg bg-white p-6" onMouseDown={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-medium text-gray-900">Nouvelle ordonnance</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
            title="Fermer"
            disabled={isUploading}
          >
            <HiOutlineX className="h-5 w-5" />
          </button>
        </div>

        <div
          className={`flex flex-col items-center justify-center rounded-lg border-2 border-dashed px-6 py-10 text-center transition-colors ${
            isUploading
              ? "cursor-wait border-gray-200 opacity-60"
              : isDragging
                ? "cursor-pointer border-primary-400 bg-primary-50"
                : "cursor-pointer border-gray-200 hover:border-gray-300 hover:bg-gray-50"
          }`}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setIsDragging(false);
            uploadFiles(e.dataTransfer.files);
          }}
          onClick={() => !isUploading && fileInputRef.current.click()}
        >
          <input type="file" className="hidden" ref={fileInputRef} onChange={(e) => uploadFiles(e.target.files)} accept="image/*,application/pdf" multiple />
          {isUploading ? (
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-primary-500" />
          ) : (
            <HiOutlineUpload className="h-8 w-8 text-gray-300" />
          )}
          <p className="mt-3 text-sm text-gray-600">{isUploading ? "Extraction en cours..." : "Glissez-déposez des fichiers ici ou cliquez pour parcourir"}</p>
          <p className="mt-1 text-xs text-gray-400">Images ou PDF · plusieurs fichiers possibles</p>
        </div>

        <label className="mt-4 flex cursor-pointer items-center justify-between gap-4 rounded-lg border border-gray-100 px-4 py-3">
          <span>
            <span className="block text-sm text-gray-800">Mode formulaire</span>
            <span className="block text-xs text-gray-400">Extraction adaptée aux ordonnances pré-imprimées</span>
          </span>
          <input
            type="checkbox"
            className="h-4 w-4 shrink-0 cursor-pointer rounded border-gray-300 text-primary-500 focus:ring-1 focus:ring-primary-500"
            checked={formMode}
            onChange={() => setFormMode(!formMode)}
            disabled={isUploading}
          />
        </label>
      </div>
    </div>
  );
};

export default UploadPrescriptionModal;
